import { LitElement, html, css, nothing } from 'lit';
import './theme.css';
import { connectToHost as realConnectToHost, HostTimeoutError } from './host-adapter.js';
import { loadOriginContext as realLoadOriginContext } from './api.js';
import { productionOrders } from './orders.js';
import { installStubHostAdapter } from './stub-host-adapter.js';
import { createStubOrders } from './stub-orders.js';
import { createStubOriginLoader, stubExistingSeed } from './stub-origin.js';
import { emphasiseMissing, emphasiseTerms } from './emphasis.js';
import {
  STATES, LABELS, DIALOGS, TOASTS, PRODUCT, stateForError, pillForOrder, toneForStatus, displayStatus,
  stateForDelivery,
} from './states.js';
import { base, buttons, screen, cards, callouts, pills, centerState } from './styles.js';
import { lifecycleStyles, stepper, deliveredList, orderSummaryRows } from './lifecycle.js';
import { formatDate } from './components/loan-card.js';

/** F2: how long the completed screen keeps asking for ICE's verdict on our last writes. ICE's
 *  first retry is about 15 minutes after a locked write, but its retry EVENT arrives at once. */
const ICE_WATCH_MS = 5 * 60_000;
import { DEFAULT_ORDER } from './components/order-options.js';
import './components/loan-card.js';
import './components/documents-panel.js';
import './components/order-options.js';
import './components/confirm-dialog.js';
import './components/toast-stack.js';

// public/ is copied into dist/ verbatim; BASE_URL is './' in a build (vite.config.js) so the
// path survives an interfaceUrl that carries a directory, exactly as the bundle's own assets do.
const LOGO_URL = `${import.meta.env?.BASE_URL ?? './'}pbg-logo.png`;

const RECONNECT_DELAY_MS = 3000;

// How long an order may sit without an order number before the officer is told, once, that this
// is normal. Long enough that a healthy webhook round trip never trips it.
const DELAYED_AFTER_MS = 60000;

// How long a 404 on the status poll still means "not received yet" rather than "gone". The host
// creates the transaction and ICE's webhook reaches our BFF a few seconds later, so the first poll
// can find nothing (found live 2026-10-19: polls at 17:03:20 and 18:30:51, webhooks 1-3 s later).
// Long enough for a slow webhook; past it, a missing order is a real problem and polling stops.
const NOT_YET_RECEIVED_MS = 2 * 60_000;

/**
 * The whole of the partner iframe's UI: one lit element that owns which screen the officer is
 * looking at (see STATES in states.js for the table and the copy). Every dependency is injectable
 * through `config`, which is what lets the state machine be tested without Encompass, without a
 * network and without a backend.
 *
 * Around the states sits the handoff's shell: a header with the firm, the product and a live
 * status pill, and on the order screens a persistent footer whose Close and Cancel are always
 * available (cert 1.13), each behind a confirm dialog.
 *
 * Two invariants the machine enforces rather than trusts:
 *   - `failed` is only ever entered from `processing` (cert 1.22) -- see transition();
 *   - `sessionExpired` is only ever entered on PAT_REJECTED -- see stateForError().
 *
 * The partner access token is held in a private field and is never rendered, never logged and
 * never put in a URL. It is a live credential for someone else's system.
 */
export class EpcApp extends LitElement {
  static properties = {
    state: { type: String },
    context: { type: Object },
    documents: { type: Array },
    error: { type: Object },
    // newOrder has two views: the loan context (screen 2) and the options form (screen 3).
    view: { type: String },
    order: { type: Object },
    orderRef: { type: Object },
    // The references of the orders this one replaces. A retry is a NEW order (there is no re-run
    // in ICE's model), and the order it replaces stays on file under its own number -- the number
    // the officer is told to quote to support. Losing it with the orderRef would leave them with
    // no handle on the order that actually went wrong.
    supersededRefs: { type: Array },
    // A create timed out and we do not know whether Encompass took it. The toast that says so is
    // gone in seconds; this is what keeps the warning on screen 3, next to the button that would
    // place the second order, until an order actually goes through or the iframe reloads.
    createTimedOut: { type: Boolean },
    status: { type: Object },
    busy: { type: String },
    pollStopped: { type: Boolean },
    previousState: { type: String },
    // cert 1.15's outcome and its provenance, reflected as data-host-tx / data-host-tx-src. They
    // render nothing; they exist so the sandbox tester and Playwright can see a call that succeeds
    // silently, fails silently and is refused silently -- see #announceTransaction.
    hostTransaction: { type: String },
    hostTransactionSource: { type: String },
    dialog: { state: true },
    logoFailed: { state: true },
  };

  static styles = [base, buttons, screen, cards, callouts, pills, centerState, lifecycleStyles, css`
    :host {
      display: flex; flex-direction: column; height: 100vh; min-height: 0;
      background: var(--pbg-canvas); color: var(--pbg-ink);
    }

    /* header */
    .pbg-header {
      flex: 0 0 auto; display: flex; align-items: center; gap: 10px; padding: 10px 16px;
      background: var(--pbg-surface); border-bottom: 1px solid var(--pbg-gray-200);
    }
    .pbg-logo { height: 30px; width: auto; display: block; }
    .pbg-logo[hidden] { display: none; }
    .pbg-wordmark { display: inline-flex; align-items: center; gap: 8px; }
    .pbg-wordmark .mark {
      width: 30px; height: 30px; border-radius: 6px; flex: 0 0 auto;
      background: linear-gradient(135deg, var(--pbg-navy), var(--pbg-blue));
      color: #fff; font-weight: 700; font-size: 12px; letter-spacing: .02em;
      display: flex; align-items: center; justify-content: center;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .12);
    }
    .pbg-head-titles { display: flex; flex-direction: column; line-height: 1.15; min-width: 0; }
    .pbg-head-titles .firm { font-size: 11px; color: var(--pbg-gray-700); font-weight: 500; }
    .pbg-head-titles .product {
      font-size: 13.5px; font-weight: 600; color: var(--pbg-navy);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .pbg-head-spacer { flex: 1; }
    .pbg-head-status { font-size: 11px; font-weight: 600; padding: 4px 9px; }
    .pbg-head-status .sd { width: 7px; height: 7px; }

    /* scrolling body */
    .pbg-body { flex: 1 1 auto; overflow-y: auto; padding: var(--pbg-space-body); scroll-behavior: smooth; }
    @media (prefers-reduced-motion: reduce) { .pbg-body { scroll-behavior: auto; } }

    /* persistent footer -- close/cancel ALWAYS available on the order screens (cert 1.13) */
    .pbg-footer {
      flex: 0 0 auto; display: flex; align-items: center; gap: 10px; padding: 10px 16px;
      background: var(--pbg-surface); border-top: 1px solid var(--pbg-gray-200);
    }
    .pbg-footer .foot-note { font-size: 11px; color: var(--pbg-gray-400); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .pbg-footer .foot-spacer { flex: 1; }

    /* narrow (360) */
    @media (max-width: 480px) {
      .pbg-head-titles .firm { display: none; }
      .pbg-head-status { display: none; }
      .pbg-body { padding: 14px; }
      .pbg-footer .foot-note { display: none; }
    }

    .callout + .actions, .card + .callout, .callout + .card, .callout + .callout { margin-top: var(--pbg-space-gap); }
    .readiness-list { margin: 4px 0 0; padding-left: 18px; }
    .readiness-list li + li { margin-top: 2px; }
    epc-order-options + .callout { margin-top: var(--pbg-space-gap); }
    .pill-row { margin-bottom: var(--pbg-space-gap); }
    .note { margin: 0; font-size: 11.5px; color: var(--pbg-gray-400); flex-basis: 100%; }
    .existing-loan { margin-top: var(--pbg-space-gap); }
    .ro-tag.ref { color: var(--pbg-gray-400); text-transform: none; letter-spacing: 0; }
    .ro-tag.ref::before { display: none; }
  `];

  #host = null;
  #focusTitle = false;
  #pollTimer = null;
  #pollSeq = 0;
  #awaitingSince = null;
  /** F2: when the completed screen stops waiting for ICE's answer to our last writes (0 = not
   *  watching). Bounded, because each poll of a completed order costs an outbox read and an ML5 call. */
  #iceWatchUntil = 0;
  /** When the status poll first answered 404 for an order with no number yet (null = it has not). */
  #notFoundSince = null;
  #delayedNotified = false;
  #reconnectTimer = null;
  #reconnectAttempted = false;

  constructor() {
    super();
    this.state = 'connecting';
    this.previousState = null;
    this.context = null;
    this.documents = [];
    this.error = null;
    this.view = 'context';
    this.order = null;
    this.orderRef = null;
    this.supersededRefs = [];
    this.createTimedOut = false;
    this.status = null;
    this.busy = null;
    this.pollStopped = false;
    this.dialog = null;
    this.logoFailed = false;
    this.config = null;
    this.hostTransaction = null;
    this.hostTransactionSource = null;
    this.ready = Promise.resolve();
    // Mirrors `ready`, for the one call #load deliberately does NOT wait for. Nothing in the app
    // awaits it; it exists so a test can be deterministic about an outcome the officer never waits
    // on. See #announceTransaction.
    this.hostAnnounced = Promise.resolve();
  }

  connectedCallback() {
    super.connectedCallback();

    // Local development and the Playwright smoke test run against the documented stubs rather
    // than a real Encompass session and BFF. Query-string gated so they can never be reached from
    // the deployed iframe, whose URL we register ourselves:
    //   ?stubHost=1    the host adapter and the order lifecycle (stub-host-adapter.js, stub-orders.js)
    //   &stubOrigin=1  the origin context too, for `npm run dev` without a BFF (stub-origin.js)
    //   &forceFail=1   the stub's first lifecycle fails after processing, as the mock's control does
    const params = new URLSearchParams(location.search);
    const stub = params.get('stubHost') === '1';
    const stubOrigin = stub && params.get('stubOrigin') === '1';
    if (stub) installStubHostAdapter();

    this.config ??= {
      baseUrl: import.meta.env?.VITE_BFF_BASE_URL ?? '',
      connectToHost: realConnectToHost,
      loadOriginContext: stubOrigin ? createStubOriginLoader(params) : realLoadOriginContext,
      orders: stub
        ? createStubOrders({ forceFail: params.get('forceFail') === '1', seed: stubOrigin ? stubExistingSeed(params) : [] })
        : productionOrders,
    };
    this.config.orders ??= productionOrders;

    this.ready = this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopPolling();
    clearTimeout(this.#reconnectTimer);
  }

  /**
   * The one place the state changes. Public so a test can prove the guard: failure is only ever
   * reported after processing (cert 1.22), and the machine refuses it from anywhere else.
   */
  transition(next) {
    if (!(next in STATES)) throw new Error(`Unknown state "${next}".`);
    if (next === 'failed' && this.state !== 'processing') {
      throw new Error(`Illegal transition ${this.state} -> failed: an order fails only after processing (cert 1.22).`);
    }
    if (next !== this.state) this.previousState = this.state;
    this.state = next;
  }

  async #load() {
    this.transition('connecting');
    this.error = null;
    this.view = 'context';
    this.order = null;
    this.orderRef = null;
    this.supersededRefs = [];
    // A reload is a fresh handshake and a freshly read origin: whatever the officer was warned
    // about belongs to a session that no longer exists, and the origin's existingOrder is now the
    // authority on whether that timed-out create landed.
    this.createTimedOut = false;
    this.status = null;
    this.dialog = null;
    this.hostTransaction = null;
    this.hostTransactionSource = null;
    this.#stopPolling();
    this.#awaitingSince = null;
    this.#delayedNotified = false;

    try {
      this.#host = await this.config.connectToHost();
    } catch (error) {
      // Includes the 15 s timeout and "opened outside Encompass". Both are the same screen: there
      // is nothing the officer can do differently, and Retry is still worth offering.
      this.error = { errorCode: 'HOST_UNAVAILABLE', message: error?.message };
      this.transition('error');
      return;
    }

    this.transition('loading');

    if (!(await this.#loadContext())) return;

    // cert 1.15: if this loan already has one of our orders, tell the host which transaction this
    // iframe is showing. Started here -- before the credential branch, so a loan whose officer is
    // sent to re-enter their DocPrep credentials is still announced -- and deliberately NOT
    // awaited: see #announceTransaction for why the officer must never wait on it.
    this.hostAnnounced = this.#announceTransaction();

    if (this.context.needsCredentials) {
      this.transition('needsCredentials');
      return;
    }

    // Documents are not fetched at load: ICE's getAvailableResources is a picker dialog the officer
    // opens from the options screen (#attachDocuments), and opening it unasked during launch would
    // be a modal the officer never requested. The list starts empty and fills from their picks.
    this.documents = [];

    await this.#route();
  }

  /** The origin round trip, with its failures mapped to screens. True when the context is in. */
  async #loadContext() {
    try {
      this.context = await this.config.loadOriginContext(
        { baseUrl: this.config.baseUrl },
        { originId: this.#host.originId, partnerAccessToken: this.#host.partnerAccessToken },
      );
      return true;
    } catch (error) {
      this.error = error;
      const next = stateForError(error);
      if (next === 'sessionExpired') this.#expire();
      else this.transition(next);
      return false;
    }
  }

  async #route() {
    if (this.context?.needsCredentials) { this.transition('needsCredentials'); return; }
    // View Details: Encompass opened this iframe INSIDE one of its transactions, but the origin's
    // existingOrder only ever names an OPEN order (EpcOriginService), and a completed one is not
    // open. Without this the officer who came to look at a finished order got the New order form,
    // Start order live, on a loan that already has an order (UAT 2026-10-14, ORD-155301).
    const hostTx = this.#host?.transaction?.id ?? null;
    if (!this.context?.existingOrder && hostTx && (await this.#openHostTransaction(hostTx))) return;
    // A completed order and no open one: intake would refuse a new order as a redraw, so the form
    // is never offered (finding 5). View Details on that order took the branch above instead.
    if (!this.context?.existingOrder && this.context?.completedOrder) { this.transition('loanCompleted'); return; }
    // An origin that omits existingOrder entirely is the same as one that sends null: a falsy
    // value means no order, and the existingOrder branch is the only place it is dereferenced.
    this.transition(this.context?.existingOrder ? 'existingOrder' : 'newOrder');
    if (this.state === 'existingOrder') this.#watchExisting();
  }

  /**
   * Screen 7 follows a running order on its own (finding 7): an officer who reopens the loan lands
   * here, and it used to sit on Processing until they pressed Refresh, long after the order had
   * moved on. Quiet -- no toasts, no busy state -- at a third of the processing screen's pace, and
   * it stops on any terminal status, a session that expired, or the screen being left.
   */
  #watchExisting() {
    this.#stopPolling();
    const seq = this.#pollSeq;
    const live = () => seq === this.#pollSeq && this.state === 'existingOrder';
    const every = (this.config.orders.pollIntervalMs ?? 5000) * 3;
    const tick = async () => {
      if (!live()) return;
      const done = await this.#refreshExisting({ quiet: true });
      if (live() && !done) this.#pollTimer = setTimeout(tick, every);
    };
    if (!['completed', 'cancelled', 'failed'].includes(toneForStatus(this.context?.existingOrder?.status))) {
      this.#pollTimer = setTimeout(tick, every);
    }
  }

  /**
   * Show the transaction the host opened us in. True when it routed somewhere; false leaves the
   * caller's New order form, which is what an id the BFF will not answer for (not ours, not this
   * instance's, not yet known) has always meant. Routing mirrors the poll handler's tones; anything
   * still moving goes through #enterProcessing so the ordinary poll loop takes it from there.
   */
  async #openHostTransaction(transactionId) {
    let status;
    try {
      status = await this.config.orders.getOrderStatus({ baseUrl: this.config.baseUrl }, { ...this.#auth(), transactionId });
    } catch (error) {
      if (stateForError(error) === 'sessionExpired') { this.#expire(); return true; }
      return false;
    }
    if (!status) return false;
    this.status = status;
    this.orderRef = {
      transactionId,
      referenceNumber: status.referenceNumber ?? null,
      correlationId: status.correlationId ?? null,
    };
    const tone = toneForStatus(status.status);
    if (tone === 'cancelled') { this.transition('orderCancelled'); return true; }
    if (tone === 'completed') {
      const next = stateForDelivery(status.delivery);
      // completedDeliveryPending still moves: the push may land while the officer is looking.
      if (next === 'completedDeliveryPending') { this.transition(next); this.#startPolling(); }
      else this.transition(next);
      return true;
    }
    // processing, received and failed: failed is only legal after processing (cert 1.22), and the
    // first poll routes it there.
    this.#enterProcessing();
    return true;
  }

  /**
   * cert 1.15 (BestPractices rows 5/27/28, multi-user editing): tell the host which transaction
   * this iframe is showing, so Encompass can warn a second officer who opens the same order.
   *
   * WHICH id, and why it matters. Two sources disagree in principle:
   *   - `host.transaction.id` -- the host's own Transaction object, read at connect time. This is
   *     the transaction Encompass opened this iframe in. It is the host restating its own fact.
   *   - `context.existingOrder.transactionId` -- our BFF's answer to "is there an open order on
   *     this loan": EpcOriginService picks AN open submission by loan GUID plus instance, not the
   *     one the host is in. On a loan that has ended up with two of our transactions -- which
   *     #placeOrder's own comment says a create timeout can mint -- that is a guess.
   * The host's wins. Echoing the host's own id back can only ever confirm an association Encompass
   * already made; sending ours over the top of it would assert one it did not, which is the single
   * way this whole feature can tell Encompass something FALSE. Ours is used only where the host
   * has told us nothing, which is every case that exists today.
   *
   * A disagreement is not silently resolved, it is recorded: data-host-tx-src="mismatch" means the
   * officer is being shown the status of an order the host did not open this iframe for, which is
   * worth seeing at the sandbox gate even though we announce the right id anyway.
   *
   * NOT AWAITED by #load, on purpose. Nothing on screen depends on the answer, the adapter bounds
   * itself at 15 s, and connect and getOrigin have each already spent up to 15 s of the officer's
   * patience -- a third silent quarter-minute on the spinner buys them nothing. `hostAnnounced` is
   * how a test waits for it instead. The try/catch is what makes that safe: an unawaited promise
   * that rejects is an unhandled rejection, and the adapter's contract not to throw is the
   * adapter's, not that of every host object a test or a future wrapper may hand us.
   *
   * Not guarded against repeating. #load re-runs on Retry and after the credential handoff, and
   * both of those reconnect the host first, so a "we already told it" flag would be a flag about a
   * connection that no longer exists -- it would suppress the call on the session that needs it.
   * Re-sending the same id to the same host is what set({ id }) is for.
   */
  async #announceTransaction() {
    const hostId = this.#host?.transaction?.id ?? null;
    const ourId = this.context?.existingOrder?.transactionId ?? null;
    const id = hostId ?? ourId;

    this.hostTransactionSource = hostId
      ? (ourId && ourId !== hostId ? 'mismatch' : 'host')
      : (ourId ? 'origin' : null);

    if (!id) { this.hostTransaction = null; return; }

    // Optional call, not defensive noise: every adapter connectToHost returns has setTransaction,
    // but a host object a test or a future wrapper hands us may not. "absent" and "unsupported"
    // are different facts -- no adapter method at all, versus an adapter whose Transaction object
    // has no `set` -- and only the second one can happen in production, where it means the SDK
    // method name in host-adapter.js is wrong. That is the one thing the sandbox gate is for.
    if (typeof this.#host?.setTransaction !== 'function') { this.hostTransaction = 'absent'; return; }

    try {
      const acknowledged = await this.#host.setTransaction(id);
      this.hostTransaction = acknowledged
        ? 'set'
        : (this.#host.canSetTransaction === false ? 'unsupported' : 'refused');
    } catch {
      this.hostTransaction = 'refused';
    }
  }

  updated(changedProperties) {
    // Reflected manually (not a reactive `attribute`-mapped property) so tests -- and CI --
    // can assert against a stable machine-state hook (`[data-state="..."]`) instead of the
    // copy in states.js, which is free to change wording without breaking that gate.
    if (changedProperties.has('state')) this.setAttribute('data-state', this.state);
    if (changedProperties.has('view')) this.setAttribute('data-view', this.view);
    // Same hook, same reason, for a call with nothing on screen to show for it (cert 1.15).
    if (changedProperties.has('hostTransaction')) this.#reflect('data-host-tx', this.hostTransaction);
    if (changedProperties.has('hostTransactionSource')) this.#reflect('data-host-tx-src', this.hostTransactionSource);

    // After a transition the officer asked for, read them the new screen's title (as the mock
    // does). Not on the load-time transitions: stealing focus into an iframe on load scrolls
    // the host page.
    if (this.#focusTitle && (changedProperties.has('state') || changedProperties.has('view'))) {
      this.#focusTitle = false;
      const title = this.shadowRoot.querySelector('.scr-title, .center-state h2');
      if (title) { title.setAttribute('tabindex', '-1'); title.focus({ preventScroll: true }); }
      this.shadowRoot.querySelector('.pbg-body')?.scrollTo?.({ top: 0 });
    }
  }

  /** Present with a value, gone without one: an empty data-host-tx would read as an outcome. */
  #reflect(name, value) {
    if (value) this.setAttribute(name, value);
    else this.removeAttribute(name);
  }

  // ---------------------------------------------------------------- rendering

  render() {
    const spec = STATES[this.state];
    const pill = spec.pill ?? pillForOrder(this.context?.existingOrder);

    return html`
      <header class="pbg-header">
        <img class="pbg-logo" src=${LOGO_URL} alt=${PRODUCT.firm} ?hidden=${this.logoFailed}
             @error=${() => { this.logoFailed = true; }} />
        ${this.logoFailed ? html`<span class="pbg-wordmark" aria-hidden="true"><span class="mark">PBG</span></span>` : nothing}
        <div class="pbg-head-titles">
          <span class="firm">${PRODUCT.firm}</span>
          <span class="product">${PRODUCT.product}</span>
        </div>
        <span class="pbg-head-spacer"></span>
        <span class="status-pill pbg-head-status ${pill.tone}" data-status-pill aria-live="polite">
          <span class="sd" aria-hidden="true"></span>${pill.label}
        </span>
      </header>

      <main class="pbg-body">${this.#screen(spec)}</main>

      ${spec.footer ? html`
        <footer class="pbg-footer">
          <span class="foot-note">${PRODUCT.tagline}</span>
          <span class="foot-spacer"></span>
          <button class="btn btn-ghost btn-sm" data-action="cancel" @click=${() => this.#onAction('cancel')}>${LABELS.cancel}</button>
          <button class="btn btn-secondary btn-sm" data-action="close" @click=${() => this.#onAction('close', { confirm: true })}>${LABELS.closeFooter}</button>
        </footer>` : nothing}

      <epc-toast-stack></epc-toast-stack>
      <epc-confirm-dialog .open=${!!this.dialog}
                          .heading=${this.dialog?.title ?? ''} .body=${this.dialog?.body ?? ''}
                          .confirmLabel=${this.dialog?.confirmLabel ?? ''} .danger=${!!this.dialog?.danger}
                          .icon=${this.dialog?.icon ?? null}
                          @confirm=${this.#onDialogConfirm} @cancel=${() => { this.dialog = null; }}></epc-confirm-dialog>`;
  }

  #screen(spec) {
    switch (this.state) {
      case 'connecting':
      case 'loading':
      case 'sessionExpired':
        return this.#spinnerScreen(spec);
      case 'notOnboarded':
      case 'needsCredentials':
      case 'error':
      case 'failed':
      case 'closed':
      case 'cancelled':
      case 'orderCancelled':
      case 'loanCompleted':
        return this.#centredScreen(spec);
      case 'orderPlaced': return this.#orderPlaced(spec);
      case 'processing': return this.#processing(spec);
      case 'completed':
      case 'completedDeliveryPending':
      case 'completedDeliveryFailed':
        return this.#completed(spec);
      case 'existingOrder': return this.#existingOrder(spec);
      default:
        return this.view === 'options' ? this.#options(spec) : this.#loanContext(spec);
    }
  }

  /** Screens 1 and 11b. */
  #spinnerScreen(spec) {
    return html`
      <div class="scr">
        <div class="center-state">
          <div class="big-spin" role="status" aria-label=${spec.title}></div>
          <h2>${spec.title}</h2>
          <p>${spec.detail}</p>
          ${spec.controls.length ? html`<div class="cs-actions">${this.#buttons(spec.controls)}</div>` : nothing}
        </div>
      </div>`;
  }

  /** Screens 8, 9, 11a, 11c and the two terminal states. */
  #centredScreen(spec) {
    return html`
      <div class="scr">
        <div class="center-state">
          <div class="cs-ico ${spec.icon.tone}" aria-hidden="true">${spec.icon.glyph}</div>
          <h2>${spec.title}</h2>
          <p>${spec.detail}</p>
          ${this.state === 'failed' && this.status?.statusDetail
            // The BFF's statusDetail is a fixed officer-facing sentence, never internal text, so
            // it can be shown as it stands. It is the only account of WHY this order failed.
            ? html`<p data-field="statusDetail">${emphasiseMissing(this.status.statusDetail)}</p>`
            : nothing}
          ${this.state === 'loanCompleted' && this.context?.completedOrder?.orderReference
            ? html`<p data-field="completedReference">Order ${this.context.completedOrder.orderReference}</p>`
            : nothing}
          ${spec.controls.length ? html`<div class="cs-actions">${this.#buttons(this.#centredControls(spec))}</div>` : nothing}
          ${this.state === 'error' || this.state === 'failed' ? html`<p class="reference">${this.#referenceLine()}</p>` : nothing}
          ${this.state === 'failed' && this.#supersededLine()
            ? html`<p class="reference" data-field="supersededRefs">${this.#supersededLine()}</p>`
            : nothing}
        </div>
      </div>`;
  }

  /** View order only when the completed order is one this session can open. */
  #centredControls(spec) {
    if (this.state !== 'loanCompleted') return spec.controls;
    return spec.controls.filter((c) => c !== 'viewCompleted' || !!this.context?.completedOrder?.transactionId);
  }

  /** The support handle: the order reference when there is one, the correlation id when we have it. */
  #referenceLine() {
    const parts = [];
    if (this.orderRef?.referenceNumber) parts.push(this.orderRef.referenceNumber);
    const corr = this.status?.correlationId ?? this.orderRef?.correlationId ?? this.error?.correlationId;
    if (corr) parts.push(corr);
    return parts.length ? `Reference: ${parts.join(' · ')}` : 'Reference unavailable';
  }

  /**
   * The order (or orders) this one was placed in place of. ICE has no re-run: Try again creates a
   * second transaction, and the one it follows stays on file with PBG under its own number. Shown
   * on the processing and failed screens so that number never disappears from under the officer.
   */
  #supersededLine() {
    const refs = this.supersededRefs ?? [];
    return refs.length ? `Replaces ${refs.join(', ')}` : null;
  }

  /**
   * How to name the order a retry is about to replace. Our order number when it exists -- that is
   * what support asks for. But an order can fail before the BFF has minted one (a create that ICE
   * accepted and our webhook never heard about, a failure at step 0), and then the ICE transaction
   * id is the ONLY handle anyone has on it: it is what Encompass shows in the loan's services log
   * and what the status route is keyed on. Recording nothing, as this used to, meant a retry
   * quietly erased the officer's last trace of the order that went wrong. Named as what it is, so
   * an order number and a transaction id are never mistaken for each other on the same line.
   */
  #supersededHandle() {
    if (this.orderRef?.referenceNumber) return this.orderRef.referenceNumber;
    if (this.orderRef?.transactionId) return `Encompass transaction ${this.orderRef.transactionId}`;
    return null;
  }

  /** Screen 2 -- loan context, read only. */
  #loanContext(spec) {
    return html`
      <div class="scr">
        ${this.#head(spec.title, spec.detail)}
        <epc-loan-card .loan=${this.context?.loan} .missing=${this.#readiness?.fields ?? []}></epc-loan-card>
        ${this.#readinessCallouts(spec)}
        <div class="actions">${this.#buttons(this.#readiness?.message ? [...spec.controls, 'checkLoanAgain'] : spec.controls)}</div>
      </div>`;
  }

  /**
   * What the intake would refuse this loan for, and what DocPrep would like filled (gap 1), from
   * the origin: null when there is nothing to say. Blocking holds Start order; under the server's
   * kill switch it only warns. Recommended never holds it.
   */
  get #readiness() {
    const r = this.context?.loanReadiness;
    return r && (r.message || r.recommended?.length) ? r : null;
  }

  /**
   * The warning (what blocks) and the recommendations (what never does), or the usual read-only
   * note when there is neither. One blocking gap reads as the intake's own sentence; several are a
   * short list, one line each, because officers skim and a sentence naming eight things is a
   * paragraph. Missing items are bold either way.
   */
  #readinessCallouts(spec) {
    const r = this.#readiness;
    if (!r) {
      return html`
        <div class="callout info">
          <span class="ico" aria-hidden="true">i</span>
          <div>${spec.callout}</div>
        </div>`;
    }
    const items = r.items ?? [];
    const recommended = r.recommended ?? [];
    return html`
      ${r.message ? html`
        <div class="callout warn" data-field="loanReadiness" role="status">
          <span class="ico" aria-hidden="true">!</span>
          ${items.length > 1 ? html`
            <div>
              <div>${r.blocking ? 'Add these in Encompass, then order:' : 'Missing in Encompass:'}</div>
              <ul class="readiness-list">${items.map((i) => html`<li>${emphasiseTerms(i)}</li>`)}</ul>
            </div>`
          : html`<div>${emphasiseMissing(r.message)}</div>`}
        </div>` : nothing}
      ${recommended.length ? html`
        <div class="callout info" data-field="loanRecommended">
          <span class="ico" aria-hidden="true">i</span>
          <div>
            <div>Recommended for DocPrep (you can still order):</div>
            <ul class="readiness-list">${recommended.map((i) => html`<li>${emphasiseTerms(i)}</li>`)}</ul>
          </div>
        </div>` : nothing}`;
  }

  /** Screen 3 -- options & documents. Place order is live only where the BFF can take one. */
  #options(spec) {
    const o = spec.options;
    return html`
      <div class="scr">
        ${this.#head(o.title, o.detail(this.context?.loan?.borrowers))}
        <epc-order-options .documents=${this.documents} .schema=${this.context?.product?.optionsSchema ?? null}
                           .closingDate=${this.context?.loan?.closingDate ?? null}
                           .value=${this.order ?? { ...DEFAULT_ORDER }}
                           .attachBusy=${this.busy === 'attachDocuments'}
                           @attach-documents=${() => this.#onAction('attachDocuments')}
                           @options-changed=${(e) => { this.order = e.detail.value; }}></epc-order-options>
        ${this.createTimedOut ? html`
          <div class="callout warn" id="place-order-timeout" data-field="timedOutCallout">
            <span class="ico" aria-hidden="true">!</span>
            <div><b>${o.timedOutCalloutLead}</b> ${o.timedOutCallout}</div>
          </div>` : nothing}
        <div class="actions">
          ${this.#buttons(o.controls)}
          ${this.#placeOrderNote(o)}
        </div>
      </div>`;
  }

  /**
   * A disabled primary button owes the officer a reason. There are exactly two, and each has its
   * own note so aria-describedby can point at the one that applies:
   *   - canCreate off (the kill switch in orders.js), or
   *   - nothing ticked. The picker's own "Select at least one document" is gated on `touched`, so
   *     an officer who has just arrived on screen 3 would otherwise see a grey button and no rule.
   */
  #placeOrderNote(o) {
    if (!this.config.orders.canCreate) return html`<p class="note" id="place-order-note">${o.placeOrderNote}</p>`;
    if (this.#canPlaceOrder) return nothing;
    return html`<p class="note" id="place-order-doc-note">${o.placeOrderDocumentNote}</p>`;
  }

  /**
   * What Place order is described by: the note explaining a disabled button, if any, and -- while
   * it is up -- the timed-out callout, because the risk it names is a property of pressing THIS
   * button. A screen reader that reaches the button by tabbing would otherwise never meet the
   * warning at all.
   */
  #placeOrderNoteId() {
    const ids = [];
    if (this.createTimedOut) ids.push('place-order-timeout');
    if (!this.config.orders.canCreate) ids.push('place-order-note');
    else if (!this.#canPlaceOrder) ids.push('place-order-doc-note');
    return ids.length ? ids.join(' ') : nothing;
  }

  /** Screen 4 -- order placed. */
  #orderPlaced(spec) {
    return html`
      <div class="scr">
        ${this.#head(spec.title, spec.detail)}
        <div class="callout success">
          <span class="ico" aria-hidden="true">✓</span>
          <div><b>${spec.calloutLead}</b> ${spec.callout}</div>
        </div>
        <div class="card">
          <div class="card-head"><span class="ch-title">Order reference</span></div>
          <div class="card-body">
            <div class="order-ref" data-field="referenceNumber">${this.orderRef?.referenceNumber ?? '—'}</div>
            <hr class="divider" />
            <div class="field-rows">
              ${orderSummaryRows({ loanNumber: this.context?.loan?.loanNumber, order: this.order, documents: this.#selectedDocs() })}
            </div>
          </div>
        </div>
        <div class="actions">${this.#buttons(spec.controls)}</div>
      </div>`;
  }

  /** Screen 5 -- processing, with the honest three-step stepper. */
  #processing(spec) {
    return html`
      <div class="scr">
        ${this.#head(spec.title, spec.detail)}
        <div class="pill-row"><span class="status-pill processing"><span class="sd" aria-hidden="true"></span>Processing</span></div>
        <div class="callout info">
          <span class="ico" aria-hidden="true">i</span>
          <div><b>${spec.calloutLead}</b> ${spec.callout}</div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="ch-title">Progress</span>
            <span class="ch-spacer"></span>
            <span class="ro-tag ref">Ref ${this.orderRef?.referenceNumber ?? '—'}</span>
          </div>
          <div class="card-body">
            ${this.#supersededLine()
              ? html`<p class="note" data-field="supersededRefs">${this.#supersededLine()}</p>`
              : nothing}
            ${stepper(this.status?.step ?? 0)}
            ${this.#awaitingReference
              ? html`<p class="note" data-field="awaitingReference">${spec.awaitingReference}</p>`
              : nothing}
          </div>
        </div>
        ${this.#deliveredSoFar(this.status?.returnDocuments)}
        ${this.#pendingChangeCallout()}
        ${this.#iceDeliveryCallout(this.status)}
        ${this.#documentsPanel()}
        ${this.pollStopped ? html`<div class="actions">${this.#buttons(['refresh'])}</div>` : nothing}
      </div>`;
  }

  /**
   * F4 machinery. The officer changed the order in Encompass and the BFF recorded it without
   * applying it. Rendered ONLY from `pendingChange`, only here on the processing screen: screen 7
   * (`#existingOrder`) reads `context.existingOrder` instead of `this.status`, and
   * `#refreshExisting` merges `pendingChange` into `context.existingOrder` where nothing renders
   * it, so an officer returning to the loan sees nothing -- a known gap tracked as work item #769,
   * not a decision. `warn`, not `info`: it is not progress.
   *
   * The server owns the sentence and the iframe renders it unchanged. The iframe additionally
   * composes a short bold lead ("Change received", plus the date when present) -- the one piece of
   * officer-visible copy authored here -- so a copy change touches both this method and
   * `PendingChangeView.NotAppliedMessage`. The date stays locally formatted rather than moving
   * server-side.
   */
  /**
   * F2. What ICE did with PBG's latest write on this loan. The server owns the sentence
   * (IceDeliveryState in the BFF); the iframe only picks the tone and a short lead. Only "retrying"
   * and "error" render: "pending" is the normal few seconds after every write and would flash, and
   * "delivered" needs no words. `source` is this.status on the processing and completed screens and
   * context.existingOrder on screen 7, which #refreshExisting fills from the same poll.
   */
  #iceDeliveryCallout(source) {
    const state = source?.iceDelivery;
    const message = source?.iceDeliveryMessage;
    if (!message || (state !== 'retrying' && state !== 'error')) return nothing;
    const error = state === 'error';
    return html`<div class="callout ${error ? 'danger' : 'warn'}" data-field="iceDelivery" role="status">
      <span class="ico" aria-hidden="true">!</span>
      <div><b>${error ? 'Not saved to the loan.' : 'Waiting on Encompass.'}</b> ${message}</div>
    </div>`;
  }

  #pendingChangeCallout() {
    const change = this.status?.pendingChange;
    if (!change?.message) return nothing;
    const when = change.receivedUtc
      ? new Date(change.receivedUtc).toLocaleDateString(undefined, { dateStyle: 'medium' })
      : null;
    return html`<div class="callout warn" data-field="pendingChange" role="status">
      <span class="ico" aria-hidden="true">!</span>
      <div><b>Change received${when ? ` ${when}` : ''}.</b> ${change.message}</div>
    </div>`;
  }

  /**
   * F2's documents panel, shown on the processing screen once the BFF reports the block. Hidden
   * until then rather than rendered with zeros: an older backend that does not send `documents`
   * would otherwise read to the officer as "none attached", which is the one wrong answer.
   *
   * `expected` is what the officer picked at order time, so "1 of 3" counts against their own
   * selection rather than against whatever we happen to have managed so far.
   */
  #documentsPanel() {
    const docs = this.status?.documents;
    if (!docs) return nothing;

    const picked = Array.isArray(this.order?.documents) ? this.order.documents.length : 0;
    const expected = Math.max(picked, (docs.received ?? 0) + (docs.failed ?? 0));

    return html`<documents-panel
      .received=${docs.received ?? 0}
      .failed=${docs.failed ?? 0}
      .expected=${expected}
      .failedNames=${docs.names ?? []}></documents-panel>`;
  }

  /** Screen 6 -- completed, in its three delivery variants. */
  #completed(spec) {
    const delivery = this.status?.delivery ?? null;
    const failedDelivery = delivery === 'failed';
    return html`
      <div class="scr">
        ${this.#head(spec.title, spec.detail)}
        <div class="pill-row">
          <span class="status-pill ${spec.pill.tone}"><span class="sd" aria-hidden="true"></span>${spec.pill.label}</span>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="ch-title">${failedDelivery ? 'Order' : 'Delivered documents'}</span>
            <span class="ch-spacer"></span>
            <span class="ro-tag ref">Ref ${this.orderRef?.referenceNumber ?? '—'}</span>
          </div>
          <div class="card-body">
            ${failedDelivery
              // Nothing has actually reached the eFolder here -- not the officer's own selection,
              // and not even a real `returnDocuments` manifest, because ML5 can hold documents it
              // generated that ICE never accepted. Listing anything under "Delivered documents"
              // would contradict the screen's own headline, so the manifest is never read at all.
              ? html`<p class="dl-empty">Nothing has reached the eFolder yet. This screen will update once delivery succeeds.</p>`
              : deliveredList(this.#deliveredDocs())}
          </div>
        </div>
        ${this.#iceDeliveryCallout(this.status)}
        ${spec.redrawNote
          ? html`
              <div class="callout info">
                <span class="ico" aria-hidden="true">i</span>
                <div>${spec.redrawNote} <span class="phase2">Phase 2</span></div>
              </div>`
          : nothing}
        <div class="actions">${this.#buttons(spec.controls)}</div>
      </div>`;
  }

  /** Screen 7 -- the order this loan already has, in its processing or completed variant. */
  #existingOrder(spec) {
    const order = this.context?.existingOrder ?? {};
    const pill = pillForOrder(order);
    const tone = toneForStatus(order.status);
    const done = tone === 'completed';
    // Cancelled and failed are terminal too: the "Documents are being prepared" callout is only
    // true while the order is still running, and it would sit directly under a status row saying
    // Canceled -- the same contradiction the poll handler's own tone === 'cancelled' branch (above,
    // in the tick() this screen has no equivalent of) no longer produces.
    const terminal = done || tone === 'cancelled' || tone === 'failed';
    const updated = order.updatedUtc ? formatDateTime(order.updatedUtc) : null;
    // What came BACK: returnDocuments, as the completed screen reads it (finding 6). `documents` is
    // F2's counts object about what the officer SENT, never a list; the array form is an older BFF.
    const delivered = Array.isArray(order.returnDocuments) && order.returnDocuments.length
      ? order.returnDocuments.map((d) => ({ id: d.id, name: d.name, deliveredUtc: d.availableUtc }))
      : Array.isArray(order.documents) ? order.documents : [];
    return html`
      <div class="scr">
        ${this.#head(spec.title, terminal ? spec.terminalDetail : spec.detail)}
        <div class="pill-row">
          <span class="status-pill ${pill.tone}"><span class="sd" aria-hidden="true"></span>${pill.label}</span>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="ch-title">Order</span>
            <span class="ch-spacer"></span>
            <span class="ro-tag ref">Ref ${order.orderReference ?? '—'}</span>
          </div>
          <div class="card-body">
            <div class="field-rows">
              <div class="frow"><span class="k">Loan</span><span class="v mono" data-field="loanNumber">${this.context?.loan?.loanNumber || '—'}</span></div>
              <div class="frow"><span class="k">Reference</span><span class="v mono" data-field="orderReference">${order.orderReference || '—'}</span></div>
              <div class="frow"><span class="k">Status</span><span class="v" data-field="status">${order.status ? displayStatus(order.status) : '—'}${order.statusDetail ? ` · ${order.statusDetail}` : ''}</span></div>
              ${updated ? html`<div class="frow"><span class="k">Last updated</span><span class="v" data-field="updatedUtc">${updated}</span></div>` : nothing}
            </div>
            <hr class="divider" />
            ${done
              ? (delivered.length ? deliveredList(delivered) : nothing)
              : terminal
                ? nothing
                // The same stepper as the processing screen: an officer who reopens a running order
                // lands here, and "Order received" next to documents already in their eFolder was
                // defect 3 of the 2026-10-14 UAT run. An older BFF sends no step: 0, not stepper()'s 1.
                : html`${stepper(order.step ?? 0)}<div class="callout info"><span class="ico" aria-hidden="true">i</span><div>${spec.processingNote}</div></div>`}
          </div>
        </div>
        ${terminal ? nothing : this.#deliveredSoFar(order.returnDocuments)}
        ${this.#iceDeliveryCallout(order)}
        <div class="actions">${this.#buttons(done ? ['done', 'redraw'] : ['refresh'])}</div>
        <div class="existing-loan"><epc-loan-card .loan=${this.context?.loan}></epc-loan-card></div>
      </div>`;
  }

  /**
   * What reached the eFolder before Completed -- EPC Delivery lets doc prep deliver at any time, so
   * step 2 can arrive with the order still running. Nothing at all until something has: an empty
   * "Delivered so far" card would read as a delivery that failed.
   */
  #deliveredSoFar(docs) {
    if (!Array.isArray(docs) || !docs.length) return nothing;
    return html`
      <div class="card" data-field="deliveredSoFar">
        <div class="card-head"><span class="ch-title">Delivered so far</span></div>
        <div class="card-body">
          ${deliveredList(docs.map((d) => ({ id: d.id, name: d.name, deliveredUtc: d.availableUtc })))}
        </div>
      </div>`;
  }

  #head(title, detail) {
    return html`
      <div class="scr-head">
        <div>
          <h1 class="scr-title">${title}</h1>
          <p class="scr-sub">${detail}</p>
        </div>
      </div>`;
  }

  /**
   * F2, the officer's side: open ICE's eFolder picker and carry what they chose into the order.
   * Picked documents are appended (never replacing earlier picks, deduplicated by ICE's resource
   * id) and ticked at once -- the officer just chose them, so an unticked row would be a second
   * question. Closing the dialog without a choice is not an error and changes nothing. A refusal
   * or a timeout is a toast, not the error screen: the order is still placeable without
   * attachments, exactly as before the picker existed.
   */
  async #attachDocuments() {
    this.busy = 'attachDocuments';
    try {
      const picked = await this.#host.pickDocuments();
      const known = new Set(this.documents.map((d) => d.id));
      const fresh = (picked ?? []).filter((d) => d?.id && !known.has(d.id));
      if (fresh.length === 0) return;
      this.documents = [...this.documents, ...fresh];
      const selected = new Set([...(this.order?.documents ?? []), ...fresh.map((d) => d.id)]);
      this.order = { ...DEFAULT_ORDER, ...this.order, documents: [...selected] };
      this.#toast({ ...TOASTS.documentsAttached, desc: TOASTS.documentsAttached.desc(fresh.length) });
    } catch (error) {
      this.#toast({ ...TOASTS.documentsUnavailable, desc: error?.message || TOASTS.documentsUnavailable.desc });
    } finally {
      this.busy = null;
    }
  }

  #buttons(actions) {
    const cls = {
      retry: 'btn-primary', close: 'btn-secondary', updateCredentials: 'btn-primary',
      startOrder: 'btn-primary', back: 'btn-secondary', placeOrder: 'btn-primary',
      viewStatus: 'btn-primary', done: 'btn-primary', redraw: 'btn-secondary', refresh: 'btn-secondary',
      retryOrder: 'btn-primary', support: 'btn-secondary', reconnect: 'btn-secondary',
      checkLoanAgain: 'btn-secondary',
    };
    const busyLabel = {
      placeOrder: STATES.newOrder.options.placing, retryOrder: STATES.newOrder.options.placing,
      updateCredentials: LABELS.updateCredentialsBusy, refresh: LABELS.refreshBusy,
      reconnect: 'Reconnecting…', redraw: 'Submitting…', checkLoanAgain: LABELS.refreshBusy,
    };
    return html`${actions.map((action) => {
      const busy = this.busy === action;
      return html`
        <button class="btn ${cls[action]}" data-action=${action}
                ?disabled=${busy || !this.#enabled(action)}
                aria-describedby=${action === 'placeOrder' ? this.#placeOrderNoteId() : nothing}
                title=${action === 'startOrder' && this.#readiness?.blocking ? this.#readiness.message : nothing}
                @click=${() => this.#onAction(action)}>
          ${busy ? html`<span class="spin" aria-hidden="true"></span>${busyLabel[action] ?? LABELS[action]}` : LABELS[action]}
        </button>`;
    })}`;
  }

  #enabled(action) {
    if (action === 'placeOrder') return this.#canPlaceOrder;
    if (action === 'startOrder' && this.#readiness?.blocking) return false;
    if (action === 'redraw') return !!this.config.orders.canRedraw;
    return !this.busy || this.busy === action;
  }

  get #canPlaceOrder() {
    // canCreate is the kill switch (orders.js); otherwise it follows the form's own validity:
    // at least one document WHEN any were offered. An empty eFolder is orderable (2026-09-30) --
    // the rule guards against ignoring offered documents, and until ICE's document source is live
    // every sandbox loan offers none, so requiring a selection made this button permanently grey.
    // Same rule, same reasoning, as document-picker.js's `valid`.
    if (!this.config.orders.canCreate) return false;
    if ((this.documents?.length ?? 0) === 0) return true;
    return (this.order?.documents?.length ?? 0) > 0;
  }

  /** Placed, but our order number has not reached us yet. Screen 5 says so at step 0. */
  get #awaitingReference() {
    return !this.orderRef?.referenceNumber;
  }

  #selectedDocs() {
    const ids = this.order?.documents ?? [];
    return this.documents.filter((d) => ids.includes(d.id));
  }

  /**
   * What the completed screen lists. F3's `returnDocuments` is the real manifest -- what came BACK.
   * Before it existed this fell back to the officer's own selection, which is what they SENT; that
   * fallback stays for an older BFF and for the moment before the first poll lands.
   */
  #deliveredDocs() {
    const returned = this.status?.returnDocuments;
    if (Array.isArray(returned) && returned.length) {
      return returned.map((d) => ({ id: d.id, name: d.name, deliveredUtc: d.availableUtc }));
    }
    // F2's `documents` is a counts OBJECT, not a list -- never a manifest. Kept as a fallback only
    // because this array shape predates it.
    if (Array.isArray(this.status?.documents) && this.status.documents.length) return this.status.documents;
    // A completed status without a manifest: the officer's own selection is what was ordered.
    return this.#selectedDocs().map((d) => ({ id: d.id, name: d.name, target: d.type, deliveredUtc: this.status?.updatedUtc }));
  }

  #orderPayload() {
    const o = this.order ?? { ...DEFAULT_ORDER };
    return {
      requestType: o.requestType, packageType: o.packageType, rush: !!o.rush,
      instructions: o.instructions ?? '', extra: o.extra ?? {},
      // `type` is the eFolder's own classification, kept for the confirmation screen's summary;
      // `mimeType` is what ICE's resources carry, and the eFolder does not always state one.
      documents: this.#selectedDocs().map((d) => ({ id: d.id, name: d.name, type: d.type, mimeType: d.mimeType })),
    };
  }

  #auth() {
    return { partnerAccessToken: this.#host?.partnerAccessToken, originId: this.#host?.originId };
  }

  // ---------------------------------------------------------------- actions

  #onAction(action, { confirm = false } = {}) {
    switch (action) {
      case 'retry':
        this.#focusTitle = true;
        this.ready = this.#load();
        return;
      case 'updateCredentials':
        this.ready = this.#updateCredentials();
        return;
      case 'startOrder':
        this.#focusTitle = true;
        this.view = 'options';
        return;
      case 'attachDocuments':
        this.ready = this.#attachDocuments();
        return;
      case 'checkLoanAgain':
        this.ready = this.#checkLoanAgain();
        return;
      case 'back':
        this.#focusTitle = true;
        this.view = 'context';
        return;
      case 'placeOrder':
        this.ready = this.#placeOrder();
        return;
      case 'retryOrder':
        this.ready = this.#placeOrder({ retry: true });
        return;
      case 'viewStatus':
        this.#focusTitle = true;
        this.#enterProcessing();
        return;
      case 'viewCompleted':
        this.ready = this.#viewCompleted();
        return;
      case 'refresh':
        this.ready = this.state === 'processing' ? this.#resumePolling() : this.#refreshExisting();
        return;
      case 'redraw':
        this.dialog = { kind: 'redraw', ...DIALOGS.redraw };
        return;
      case 'support':
        this.#toast(TOASTS.support);
        return;
      case 'reconnect':
        this.ready = this.#reconnect();
        return;
      case 'done':
        this.dialog = { kind: 'close', ...DIALOGS.close };
        return;
      case 'cancel':
        this.dialog = this.#cancelDialog();
        return;
      case 'close':
        if (confirm) this.dialog = { kind: 'close', ...DIALOGS.close };
        else this.ready = this.#doClose();
        return;
      default:
    }
  }

  /** Which Cancel the officer gets: discard selections before an order, cancel the order through
   *  the live BFF endpoint (F5; true in both production and the stub today) while it is still
   *  genuinely cancellable, the plain close dialog once it is known terminal (the footer renders
   *  on completed/failed/existingOrder screens too, where "orderRef truthy" alone would still
   *  offer a destructive dialog promising to stop work that can only ever answer 409), and
   *  otherwise -- only if canCancel is ever rolled back to false -- an honest "return without
   *  cancelling". */
  #cancelDialog() {
    const placed = !!this.orderRef || this.state === 'existingOrder';
    if (!placed) return { kind: 'cancel', ...DIALOGS.cancel };
    const tone = toneForStatus((this.status ?? this.context?.existingOrder)?.status);
    // Known terminal client-side: cancelling can only 409, so this is a plain close, not a
    // destructive dialog that cannot work. The 409 CANNOT_CANCEL branch in #doCancelOrder stays as
    // the backstop for the genuine race -- the order term became terminal after this check ran.
    if (tone === 'completed' || tone === 'cancelled' || tone === 'failed') return { kind: 'close', ...DIALOGS.close };
    if (this.config.orders.canCancel) return { kind: 'cancelOrder', ...DIALOGS.cancelOrder };
    return { kind: 'cancelUnavailable', ...DIALOGS.cancelUnavailable };
  }

  #onDialogConfirm = () => {
    const kind = this.dialog?.kind;
    this.dialog = null;
    switch (kind) {
      case 'cancel': this.ready = this.#doCancel(); break;
      case 'cancelOrder': this.ready = this.#doCancelOrder(); break;
      case 'redraw': this.ready = this.#doRedraw(); break;
      default: this.ready = this.#doClose();
    }
  };

  async #updateCredentials() {
    this.busy = 'updateCredentials';
    try {
      // cert 1.14: the host owns the credential flow; we only ask for it, then read the origin
      // again to learn whether it worked.
      await this.#host?.performAction('updateCredentials');
      this.#toast(TOASTS.credentialsHandoff);
    } finally {
      this.busy = null;
    }
    await this.#load();
  }

  async #placeOrder({ retry = false } = {}) {
    if (!this.config.orders.canCreate) return;
    if (!retry) {
      const options = this.shadowRoot.querySelector('epc-order-options');
      if (!options?.valid) { options?.reportValidity(); return; }
    }
    this.busy = retry ? 'retryOrder' : 'placeOrder';
    // Captured BEFORE the create: on a retry the orderRef about to be replaced belongs to the
    // order that failed, and its referenceNumber is the only handle the officer (and support) has
    // on it. See supersededRefs.
    const supersededRef = this.#supersededHandle();
    try {
      // The host is a dependency of creating an order, not of reading one: in production
      // createOrder calls transaction.create and never touches our API (cert 1.1).
      const result = await this.config.orders.createOrder(
        { baseUrl: this.config.baseUrl, host: this.#host },
        { ...this.#auth(), order: this.#orderPayload() },
      );
      if (supersededRef) this.supersededRefs = [...this.supersededRefs, supersededRef];
      // An order went through, so whatever the last timed-out create may or may not have done is
      // no longer the officer's live decision: they have a confirmed order in front of them, and
      // the standing warning on screen 3 has done its job.
      this.createTimedOut = false;
      this.orderRef = {
        transactionId: result?.transactionId,
        referenceNumber: result?.referenceNumber ?? null,
        correlationId: result?.correlationId ?? null,
      };
      this.status = null;
      this.#focusTitle = true;
      // Screen 4 is the ORDER NUMBER screen, so it can only be shown once there is one. The host
      // hands back a transaction id and nothing else, so production waits on screen 5 until the
      // first status carries a reference; the stub knows the number at once and goes straight
      // there, as does anything else that can answer with one.
      if (!retry && this.orderRef.referenceNumber) this.transition('orderPlaced');
      else this.#enterProcessing();
    } catch (error) {
      // A rejected create is NOT the failed screen: nothing was placed, so there is nothing that
      // failed after processing (cert 1.22). The officer stays where they were, told why.
      if (stateForError(error) === 'sessionExpired') this.#expire();
      // A TIMEOUT is not a refusal, and must never be reported as one. The host adapter keeps this
      // error's own type precisely because Encompass may have created the transaction and merely
      // failed to answer in time (host-adapter.js, createTransaction). placeFailed's "Nothing was
      // sent. Please try again in a moment." would be false here, and acting on it would mint a
      // SECOND ICE transaction on the loan -- so this toast reports the unknown and sends the
      // officer to check the loan before ordering again, rather than inviting a retry.
      //
      // The toast alone is not enough: it dismisses itself, and four seconds later the officer is
      // back on screen 3 with Place order live and nothing on the screen to stop them. So the same
      // warning is latched onto the options view, where it outlives the toast and sits beside the
      // button it is about, until an order goes through or the iframe reloads.
      else if (error instanceof HostTimeoutError) {
        this.createTimedOut = true;
        this.#toast(TOASTS.placeTimedOut);
      } else this.#toast(TOASTS.placeFailed);
    } finally {
      this.busy = null;
    }
  }

  #enterProcessing() {
    this.transition('processing');
    this.pollStopped = false;
    // Only an order still waiting for its number is "delayed" after a minute; one that already
    // has a reference is just taking its time preparing documents, which screen 5 already says.
    this.#awaitingSince = this.orderRef?.referenceNumber ? null : Date.now();
    this.#delayedNotified = false;
    this.#startPolling();
  }

  async #resumePolling() {
    this.pollStopped = false;
    this.#startPolling();
  }

  /** Status is pulled, not pushed: the BFF learns of progress from ICE's webhook and we ask it. */
  #startPolling() {
    this.#stopPolling();
    this.#notFoundSince = null;
    const seq = this.#pollSeq;
    // completedDeliveryPending is the one terminal-looking state that is not terminal -- the push
    // is still running and the screen must update when it lands. `completed` and
    // completedDeliveryFailed genuinely are terminal and must NOT keep polling: each poll of a
    // completed order costs an outbox read and a live ML5 Document API call.
    const live = () => seq === this.#pollSeq
      && (this.state === 'processing' || this.state === 'completedDeliveryPending'
        || (this.state === 'completed' && this.#iceWatchUntil > 0));
    const tick = async () => {
      if (!live()) return;
      let status;
      try {
        status = await this.config.orders.getOrderStatus(
          { baseUrl: this.config.baseUrl },
          { ...this.#auth(), transactionId: this.orderRef?.transactionId },
        );
      } catch (error) {
        if (!live()) return;
        if (stateForError(error) === 'sessionExpired') { this.#expire(); return; }
        // A 404 before our order number exists means ICE's webhook has not reached us yet, not that
        // the order is gone: keep polling, for a bounded time (NOT_YET_RECEIVED_MS).
        if (error?.status === 404 && !this.orderRef?.referenceNumber) {
          this.#notFoundSince ??= Date.now();
          if (Date.now() - this.#notFoundSince < NOT_YET_RECEIVED_MS) {
            this.#pollTimer = setTimeout(tick, this.config.orders.pollIntervalMs ?? 5000);
            return;
          }
        }
        this.pollStopped = true;
        this.#toast(TOASTS.statusUnavailable);
        return;
      }
      if (!live()) return;
      this.#notFoundSince = null;
      this.status = status;

      // Our order number is minted after the order is accepted, so it arrives on a status rather
      // than on the create. Adopt it the moment it appears, whatever the status says -- screens
      // 5, 6 and 8 all quote it, and the failed screen is the one where the officer needs it most.
      const arrived = !this.orderRef?.referenceNumber && !!status?.referenceNumber;
      if (arrived) {
        this.orderRef = {
          ...this.orderRef,
          referenceNumber: status.referenceNumber,
          correlationId: status.correlationId ?? this.orderRef?.correlationId ?? null,
        };
        this.#awaitingSince = null;
      }

      const tone = toneForStatus(status?.status);
      if (tone === 'completed') {
        const next = stateForDelivery(status?.delivery);
        // Only on a real change: this branch now runs on EVERY poll while delivery is pending, and
        // re-entering the same state each tick would re-fire the screen's focus handling.
        if (next !== this.state) {
          this.transition(next);
          // The "documents delivered" toast belongs only to the screen that says they were. Firing
          // it beside "we couldn't deliver to your eFolder" would contradict the screen it lands on.
          if (next === 'completed') this.#toast(TOASTS.delivered);
        }
        // completed and completedDeliveryFailed are genuinely terminal: no further poll. Only
        // completedDeliveryPending schedules its own next tick here, rather than falling through to
        // the arrived/delayed-toast logic below -- the reference number can arrive on this very
        // poll, and falling through would let the "just arrived" branch downstream overwrite this
        // transition with 'orderPlaced'.
        if (next === 'completedDeliveryPending') {
          this.#pollTimer = setTimeout(tick, this.config.orders.pollIntervalMs ?? 5000);
          return;
        }
        // F2 (final review, Important 2). On `completed` our last writes are seconds old, so ICE's
        // verdict on them is usually still "pending" -- and its retry, when the loan is open in
        // Encompass, lands after that. Keep watching, slowly and for a bounded time, until ICE
        // answers; retrying, error and delivered are all answers and end the watch.
        if (next === 'completed' && status?.iceDelivery === 'pending') {
          if (!this.#iceWatchUntil) this.#iceWatchUntil = Date.now() + ICE_WATCH_MS;
          if (Date.now() < this.#iceWatchUntil) {
            this.#pollTimer = setTimeout(tick, (this.config.orders.pollIntervalMs ?? 5000) * 3);
            return;
          }
        }
        this.#iceWatchUntil = 0;
        return;
      }
      // Terminal, and NOT a failure. The order ended because someone ended it -- in another tab, or
      // in Encompass, never here (that path is #doCancelOrder's own success, which lands on
      // 'cancelled') -- so no host.cancel()/close was dispatched, and orderCancelled says so.
      if (tone === 'cancelled') { this.transition('orderCancelled'); return; }
      if (tone === 'failed') { this.transition('failed'); this.#toast(TOASTS.preparationFailed); return; }

      // The order number is the confirmation, so the confirmation screen is what the first status
      // carrying one buys. Polling stops here by design: screen 4's View status starts it again.
      if (arrived) { this.#focusTitle = true; this.transition('orderPlaced'); return; }

      // A minute without a number. Say so once, then keep polling: nothing has gone wrong, and
      // the officer is free to leave -- the order is Encompass's now and continues without us.
      if (this.#awaitingSince !== null && !this.#delayedNotified
          && Date.now() - this.#awaitingSince >= DELAYED_AFTER_MS) {
        this.#delayedNotified = true;
        this.#toast(TOASTS.delayed);
      }

      this.#pollTimer = setTimeout(tick, this.config.orders.pollIntervalMs ?? 5000);
    };
    tick();
  }

  #stopPolling() {
    this.#pollSeq += 1;
    this.#iceWatchUntil = 0;
    clearTimeout(this.#pollTimer);
    this.#pollTimer = null;
    // NOT #awaitingSince: #startPolling calls this first, and Refresh status must not restart the
    // "still working" clock. #load and #enterProcessing are what own it.
  }

  /** Merge a fresh status into screen 7. True once the order is terminal (or the session ended),
   *  which is what tells #watchExisting to stop. `quiet` is the watcher's: no busy state, no toasts. */
  async #refreshExisting({ quiet = false } = {}) {
    const existing = this.context?.existingOrder;
    if (!existing?.transactionId) return true;
    if (!quiet) this.busy = 'refresh';
    try {
      const status = await this.config.orders.getOrderStatus(
        { baseUrl: this.config.baseUrl },
        { ...this.#auth(), transactionId: existing.transactionId },
      );
      // The refresh replaces what the origin said, detail included: a stale "Generating documents"
      // next to a fresh "Completed" would be two truths on one row.
      this.context = {
        ...this.context,
        existingOrder: {
          ...existing,
          ...status,
          status: status.status ?? existing.status,
          statusDetail: status.statusDetail ?? null,
          orderReference: status.referenceNumber ?? existing.orderReference,
        },
      };
      // "Still processing" belongs only to an order that is actually still running: completed,
      // cancelled and failed are all terminal, and firing it next to a Cancelled status row would
      // be the same contradiction the poll handler used to produce.
      const tone = toneForStatus(status?.status);
      const terminal = ['completed', 'cancelled', 'failed'].includes(tone);
      if (!terminal && !quiet) this.#toast(TOASTS.stillProcessing);
      return terminal;
    } catch (error) {
      if (stateForError(error) === 'sessionExpired') { this.#expire(); return true; }
      if (!quiet) this.#toast(TOASTS.statusUnavailable);
      return false; // the watcher simply tries again next time
    } finally {
      if (!quiet) this.busy = null;
    }
  }

  /** Finding 5's way out: open the loan's completed order, as View Details would have. */
  async #viewCompleted() {
    const transactionId = this.context?.completedOrder?.transactionId;
    if (!transactionId || !(await this.#openHostTransaction(transactionId))) this.#toast(TOASTS.statusUnavailable);
  }

  async #doRedraw() {
    const transactionId = this.orderRef?.transactionId ?? this.context?.existingOrder?.transactionId;
    // The officer has just confirmed a destructive action; closing the dialog and doing nothing
    // visible is the one response that teaches them nothing. The guard itself is right -- a blank
    // id reads back as a genuine cancellation, because the endpoint answers 202 for a transaction
    // it does not know.
    if (!transactionId) { this.#toast(TOASTS.cancelFailed); return; }
    this.busy = 'redraw';
    try {
      const result = await this.config.orders.redraw({ baseUrl: this.config.baseUrl }, { ...this.#auth(), transactionId });
      this.orderRef = {
        transactionId,
        referenceNumber: result?.referenceNumber ?? this.orderRef?.referenceNumber ?? this.context?.existingOrder?.orderReference,
        correlationId: result?.correlationId,
      };
      this.status = null;
      this.#focusTitle = true;
      this.#enterProcessing();
      this.#toast(TOASTS.redrawSubmitted);
    } catch (error) {
      if (stateForError(error) === 'sessionExpired') this.#expire();
      else this.#toast(TOASTS.redrawFailed);
    } finally {
      this.busy = null;
    }
  }

  /**
   * Check again (gap 1): the officer has fixed the loan in Encompass with this iframe still open.
   * A fresh origin (refreshOrigin, which also renews the PAT) and the context again, then the same
   * routing as a first load -- an order placed meanwhile from elsewhere still wins over the form.
   */
  async #checkLoanAgain() {
    if (this.busy) return;
    this.busy = 'checkLoanAgain';
    try {
      if (this.#host?.refreshOrigin) await this.#host.refreshOrigin();
    } catch { /* a stale origin is still worth re-reading; #loadContext reports what it cannot read */ }
    const ok = await this.#loadContext();
    this.busy = null;
    if (ok) await this.#route();
  }

  // ---------------------------------------------------------------- session (11b)

  #expire() {
    this.#stopPolling();
    this.transition('sessionExpired');
    // One automatic attempt, as the mock does; after that the officer has Reconnect now.
    clearTimeout(this.#reconnectTimer);
    if (!this.#reconnectAttempted) {
      this.#reconnectTimer = setTimeout(() => { this.ready = this.#reconnect(); }, RECONNECT_DELAY_MS);
    }
  }

  async #reconnect() {
    clearTimeout(this.#reconnectTimer);
    if (this.busy === 'reconnect') return;
    this.busy = 'reconnect';
    this.#reconnectAttempted = true;
    try {
      // transaction.refreshOrigin when the host has it (a fresh PAT for the same origin);
      // otherwise the full handshake again.
      if (this.#host?.refreshOrigin) await this.#host.refreshOrigin();
      else this.#host = await this.config.connectToHost();
    } catch (error) {
      this.error = { errorCode: 'HOST_UNAVAILABLE', message: error?.message };
      this.busy = null;
      this.transition('error');
      return;
    }
    const ok = await this.#loadContext();
    this.busy = null;
    if (!ok) return; // still expired (stays here, with Reconnect now) or a different error screen
    this.#reconnectAttempted = false;

    // cert 1.15 again, and for two reasons rather than one. The obvious one is the branch above
    // that reconnects: a NEW connection has never been told anything. The one that actually bites
    // is the other branch -- refreshOrigin keeps the connection, but the origin has just been read
    // again, so this is where a session that expired on a fresh loan can route to existingOrder
    // for the FIRST time, with an id the host has never heard. Both branches end here, so the
    // announcement goes here rather than beside either one.
    //
    // (Reachability, checked rather than assumed: connectToHost defines refreshOrigin
    // unconditionally, and so does the stub, so `this.#host?.refreshOrigin` is falsy only when
    // #host is null -- which #load has ruled out by the time anything can call #expire. The
    // reconnect branch is therefore dead against both real adapters today. It is still the branch
    // that would break the rule if a future adapter made refreshOrigin conditional, and this call
    // covers it for free.)
    this.hostAnnounced = this.#announceTransaction();

    this.#toast(TOASTS.reconnected);
    if (this.orderRef) this.#enterProcessing(); // resume the order that was in flight
    else await this.#route();
  }

  // ---------------------------------------------------------------- close / cancel (cert 1.13)

  async #doClose() {
    this.#stopPolling();
    try { await this.#host?.close?.(); } catch { /* a host without close: the event below still fires */ }
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    this.#toast(TOASTS.returned);
    this.transition('closed');
  }

  async #doCancel() {
    // The only thing to discard before an order exists is what the officer picked.
    this.order = null;
    this.view = 'context';
    try { await this.#host?.cancel?.(); } catch { /* as above */ }
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    this.#toast(TOASTS.selectionsDiscarded);
    this.transition('cancelled');
  }

  async #doCancelOrder() {
    const transactionId = this.orderRef?.transactionId ?? this.context?.existingOrder?.transactionId;
    // The endpoint answers 202 for an unknown transaction (it cannot tell "no order" apart from
    // "not yours" without leaking which ids exist), so a blank id here would read back as a
    // genuine "Order cancelled" for an order nothing happened to.
    if (!transactionId) return;
    this.busy = 'cancel';
    try {
      await this.config.orders.cancelOrder({ baseUrl: this.config.baseUrl }, { ...this.#auth(), transactionId });
    } catch (error) {
      this.busy = null;
      if (stateForError(error) === 'sessionExpired') { this.#expire(); return; }
      // CANNOT_CANCEL: the order is already terminal. cancelFailed's "unchanged, try again" is
      // wrong on both counts here, and a retry can never succeed -- so land on the screen the
      // order actually reached instead of inviting a click that cannot work.
      if (error?.errorCode === 'CANNOT_CANCEL') {
        this.#toast({ ...TOASTS.cancelNotPossible, desc: error.detail ?? TOASTS.cancelNotPossible.desc });
        await this.#reconcileAfterCannotCancel(transactionId);
        return;
      }
      // ORIGIN_MISMATCH: a session problem, not a cancel problem -- retrying the cancel changes
      // nothing, only reopening the right loan does.
      if (error?.errorCode === 'ORIGIN_MISMATCH') { this.#toast(TOASTS.cancelSessionMismatch); return; }
      // CANCEL_CONTENDED and anything else: a transient conflict, where "try again" is correct.
      this.#toast(TOASTS.cancelFailed);
      return;
    }
    this.busy = null;
    this.#stopPolling();
    try { await this.#host?.cancel?.(); } catch { /* as above */ }
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    this.#toast(TOASTS.orderCancelled);
    this.transition('cancelled');
  }

  /** After a CANNOT_CANCEL: read the true status back and land on the screen it actually
   *  describes. Screen 7 already owns this (#refreshExisting) -- everywhere else is the live
   *  order-tracking path, so this mirrors the poll handler's own tone routing rather than
   *  reproducing #refreshExisting's context-merge shape for a state that has no existingOrder. */
  async #reconcileAfterCannotCancel(transactionId) {
    if (this.state === 'existingOrder') { await this.#refreshExisting(); return; }
    let status;
    try {
      status = await this.config.orders.getOrderStatus({ baseUrl: this.config.baseUrl }, { ...this.#auth(), transactionId });
    } catch (error) {
      // #refreshExisting already does this on the screen-7 branch above; this is its mirror here,
      // not a new decision -- a PAT rejection here means the session, not the read, and swallowing
      // it silently would strand the officer on a screen with a stale, unexplained PAT.
      if (stateForError(error) === 'sessionExpired') this.#expire();
      return; // best effort otherwise -- the toast already said why nothing changed
    }
    this.status = status;
    if (!this.orderRef?.referenceNumber && status?.referenceNumber) {
      this.orderRef = {
        ...this.orderRef,
        referenceNumber: status.referenceNumber,
        correlationId: status.correlationId ?? this.orderRef?.correlationId ?? null,
      };
    }
    const tone = toneForStatus(status?.status);
    if (tone === 'completed') { this.#stopPolling(); this.transition(stateForDelivery(status?.delivery)); return; }
    // Cancelled elsewhere, not by this click -- no host.cancel()/close was dispatched, so this is
    // orderCancelled, not 'cancelled' (see the poll handler's own branch for the same distinction).
    if (tone === 'cancelled') { this.#stopPolling(); this.transition('orderCancelled'); return; }
    // failed only ever follows processing (cert 1.22): from any other screen there is nowhere
    // legal to route to, and the toast already said why nothing changed.
    if (tone === 'failed' && this.state === 'processing') { this.#stopPolling(); this.transition('failed'); }
  }

  #toast(toast) {
    this.shadowRoot?.querySelector('epc-toast-stack')?.push(toast);
  }
}

function formatDateTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

customElements.define('epc-app', EpcApp);
